/**
 * Created by Thomas Mark
 * Date: 2021-11-18
 * 
 * Mit der Synchronisation werden Daten vom Backend in die
 * Anwendung geladen, sodass in der Anwendung komplett offline
 * gearbeitet werden kann.
 * 
 * Hier sind die sync-Aktionen der Version 2.1 implementiert.
 * In der Version 2.1 wird grundsätzlich aus dem /containers-Verzeichnis gelesen.
 * 
 * Beim Synchonisieren der Version 2.1 wird ein bestimmter Container (z.B.: Linde, Schöler, etc.) 
 * und eine bestimmte Version (z.B.: live, draft) synchronisiert. 
 * 
 * Eigentlich wird hier nicht synchronisiert, da Daten ja nur heruntergeladen werden und nicht 
 * hochgeladen werden. Aus diesem Grund wird später auch nur von Download gesprochen.
 * 
 */
import axios from "common/helpers/axios.helper"
import * as utils from "../../basics/utils";
import * as cordova from "../../basics/cordova";
import db from '../../basics/db';

/**
 * setSyncCancel()
 * 
 * Hier wird die globale Variable 'syncCancel' mit true/false belegt.
 * Während der Download läuft wird zwischendurch immer wieder überprüft, ob
 * der Benutzer den Download abgebrochen hat. Ist dies der Fall, so wird 
 * die aktuelle Datei noch heruntergeladen, erst danach wird der weitere 
 * Download nicht mehr fortgesetzt. 
 * 
 * @param {boolean} payload - true/false - Sync/Download unterbrechen bzw. nicht unterbrechen.
 */
export const setSyncCancel = (payload) => {
    if (payload === true) {
        console.log('\n\nACHTUNG: SYNC wurde abgebrochen!');
    }

    return { type: 'SYNC_CANCEL', payload: payload }
}

/**
 * setSyncType()
 * 
 * Sobald ein Download durchgeführt wird, wird diese globale Variable belegt.
 * Die Variable wird mit folgenden Werten belegt:
 * '' - wenn kein Download durchgeführt wird.
 * 'syncPortfolio' - wenn der Download der Portfolios durchgeführt wird.
 * 'syncFiles' - wenn der Download der einzelnen Dateien (png, jpg, pdf oder mp4) durchgeführt wird.
 * 
 * @param {string} type - Download-Type (siehe Beschreibung)
 */
export function setSyncType(type) {
    return {
        type: "SET_SYNC_TYPE",
        payload: type
    }
}

/**
 * setSyncAdmin()
 * 
 * Steuert den visuellen Download.
 * 
 * Diese Funktion ist zur Zeit nicht aktiv bzw. Impelementierung wurde deaktiviert.
 * 
 * @param {string} type - Download-Type (siehe Beschreibung)
 */
export function setSyncAdmin(type) {
    return {
        type: "SET_SYNC_ADMIN",
        payload: type
    }
}

/**
 * doSync()
 * 
 * Die Synchronisation bzw. der Download erfolgt in mehreren Schritten: 
 * 
 * 1.  Alte Portfolios aus LocalStorage löschen
 * 
 * 2.  Laden der toc.json
 *     Die Datei entspricht einem Inhaltsverzeichnis und enthält
 *     für jedes einzelne Portfolio eine URL.
 * 
 * 3.  Asynchrones Laden aller einzelnen Portfolios anhand der URL
 *     Über einen PromiseAll-Aufruf werden alle Portfolios gleich-
 *     zeitig vom API angefragt. 
 * 
 * 4.  Alle Dateien in LocalStorage schreiben
 *     Hier werden alle Dateien einzelnen Dateien wie
 *     png-, jpg-, pdf- oder mp4-Dateien geladen.
 *     Sobald die Daten vom API gelesen wurden, werden diese im 
 *     LocalStorage bzw. IndexedDB gespeichert.
 * 
 * 5.  Redux-Store anhand LocalStorage-Tabellen aktualisieren
 *     Dies stellt sicher, dass die Daten nach dem Sync sofort
 *     in der aktuellen Session verwendet werden können.
 * 
 * 6.  Liste der heruntergeladenen Container aktualisieren.
 * 
 * @param {string} container - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {string} version   - Version (z.B.: live, draft, etc.)
 *  
 */
export const doSync = (container, version, successCallback = null, errorCallback = null) => {
    return async (dispatch, getState) => {

        const { apiURL, token } = getState().app;
        const { syncCancel } = getState().sync;
        const { settingList } = getState().setting;
        const { downloadList } = getState().container;

        if (syncCancel) {
            console.log('doSync() syncCancel=true --> return');
            return null;
        }

        console.log(`\n\n\ndoSync(${container}, ${version}) START`);

        // ###############################################################
        // 
        // HELPER - SECTION
        // 
        // ###############################################################

        /**
         * Inhaltsverzeichnis 'toc.json' vom API lesen.
         *
         * @resolve {JSON} Portfolio
         * @reject {object} Error-Struktur
         * @return {object} Promise-Struktur
         */
        const getPortfolioTOC = (d) => {
            const requestPromise = new Promise(function (resolve, reject) {
                const url = 'toc.json';

                //console.log('get ' + url + ' from API');

                axios({
                    url: encodeURI(`${apiURL}/api/v1/file/${container}/versions/${version}/${url}`),
                    method: 'GET',
                    responseType: 'json',
                    timeout: 60 * 60 * 1000,
                    headers: { 'authorization': token },
                }).then((response) => {
                    resolve(response.data)
                }).catch((error) => {
                    reportError(error)
                    reject(error)
                })
            })
            return requestPromise
        }

        /**
         * Redux Store anhand localStorage aktualisieren
         * Damit die Daten auch sofort in der Anwendung zur Verfügung stehen
         * Siehe auch PortfoliosController Stichwort 'SYNC'
         *
         */
        const updateReduxStore = () => {

            return new Promise(function (resolve, reject) {
                cordova.readLocalPortfolios().then(async (allPortfolioList) => {
                    const downloadedContainers = utils.containersInPortfolioList(allPortfolioList);
                    dispatch({ type: 'SET_CONTAINER_LIST_DOWNLOADED', payload: downloadedContainers });

                    let newPortfolioList = [];
                    allPortfolioList.forEach((d) => {
                        const splitID_array = d.id.split('_');
                        const splitID_container = (splitID_array.length > 0) ? splitID_array[0] : null;

                        if (splitID_container.localeCompare(container) === 0) {
                            newPortfolioList.push(d);
                        }
                    });
                    dispatch({ type: 'PORTFOLIO_LIST_SUCCESS', payload: newPortfolioList });

                    const productList = utils.makeProductList(newPortfolioList);

                    const tagList = utils.makeTagList(productList);

                    dispatch({ type: 'SET_SYNC_JSON', payload: { productList: productList, tagList: tagList } });

                    dispatch({ type: 'SYNC_PORTFOLIO_SUCCESS' });

                    resolve();
                }).catch((error) => {
                    console.log('FEHLER: Portfolios konnten nicht vollständig geladen werden!', error);

                    dispatch({ type: 'SYNC_PORTFOLIO_ERROR' });

                    reject(new Error('readLocalPortfolios(error)', error));
                })
            });
        }

        /**
         * Synchronisation aller Dateien
         * Laden aller PNG-, JPG, PDF- und Video-Dateien
         */
        const downloadAllFiles = () => {
            const debug = false;

            return new Promise(function (resolve, reject) {

                cordova.readLocalPortfolios(container).then(async (newPortfolioList) => {
                    debug && console.log('newPortfolioList:', newPortfolioList);

                    const { syncCancel } = getState().sync;

                    if (syncCancel) {
                        console.log(`\n\nCANCEL downloadAllFiles()`);
                        resolve();
                        return;
                    }

                    // #################################################################
                    // Image-Files (png, jpg) sync-Liste erstellen
                    // #################################################################
                    debug && console.log('\nSYNC Image-Files\n');
                    const pngFiles = utils.getFileList(container, newPortfolioList, 'png');
                    const jpgFiles = utils.getFileList(container, newPortfolioList, 'jpg');
                    let syncFiles = [...pngFiles, ...jpgFiles];
                    let localFiles = await cordova.readLocalDirectory(container, 'image');
                    const imageFiles = syncFileDiff(container, syncFiles, localFiles, 'id');

                    debug && console.log('syncFiles:', syncFiles);
                    debug && console.log('localFiles:', localFiles);
                    debug && console.log('imageFiles:', imageFiles);

                    // #################################################################
                    // PDF-Files sync-Liste erstellen
                    // #################################################################
                    debug && console.log('\nSYNC PDF-Files\n');
                    syncFiles = utils.getFileList(container, newPortfolioList, 'pdf');
                    localFiles = await cordova.readLocalDirectory(container, 'pdf');
                    const pdfFiles = syncFileDiff(container, syncFiles, localFiles, 'id');

                    debug && console.log('syncFiles:', syncFiles);
                    debug && console.log('localFiles:', localFiles);
                    debug && console.log('pdfFiles:', pdfFiles);

                    // #################################################################
                    //
                    // Alle Iamges und PDF-Dokumente herunterladen
                    //
                    // #################################################################
                    const allSyncFiles = [...imageFiles, ...pdfFiles];

                    debug && console.log('allSyncFiles:', allSyncFiles);

                    let success = false;
                    await doSyncFiles(dispatch, getState, container, version, allSyncFiles, 10).then(() => {
                        debug && console.log('\nSYNC FILES DONE\n');
                        success = true;
                    }).catch((error) => {
                        debug && console.log('\nSYNC FILES ERROR:', error);
                        reject(new Error('doSyncFiles(error)', error));
                        return;
                    })
                    if (!success) return;

                    // #################################################################
                    //
                    // Images im REDUX-Store übertragen
                    //
                    // #################################################################
                    await cordova.loadImageBuffer(container, newPortfolioList);

                    // #################################################################
                    // Video-Files sync-Liste erstellen
                    // #################################################################

                    // Produkt-Videos aus den Portfolios lesen
                    debug && console.log('\nSYNC Video-Files\n');
                    syncFiles = utils.getFileList(container, newPortfolioList, 'mp4');

                    // Tutorial-Videos aus den Settings hinzufügen
                    debug && console.log('\nsettingList:', settingList);
                    if (settingList && settingList['tutorial']['videos']) {
                        settingList['tutorial']['videos'].forEach((d) => { syncFiles.push({ id: d, name: d, size: 0 }) });
                    }

                    // lokal gespeicherte Videos aus IndexedDB oder localStorage lesen
                    localFiles = await cordova.readLocalDirectory(container, 'video');

                    // Ableich zwischen der VideoList und den bereits gespeicherten Videos
                    let videoFiles = syncFileDiff(container, syncFiles, localFiles, 'name');

                    debug && console.log('syncFiles:', syncFiles);
                    debug && console.log('localFiles:', localFiles);
                    debug && console.log('videoFiles:', videoFiles);

                    if (window['cordova']) {
                        await doSyncVideos(dispatch, getState, container, videoFiles).then(() => {
                            debug && console.log('\nSYNC VIDEOS DONE\n');
                        }).catch((error) => {
                            debug && console.log('\nSYNC VIDEOS ERROR\n', error);
                            reject();
                            return;
                        })
                    } else {
                        await doSyncFiles(dispatch, getState, container, version, videoFiles, 1).then(() => {
                            debug && console.log('\nSYNC VIDEOS DONE\n');
                        }).catch((error) => {
                            debug && console.log('\nSYNC VIDEOS ERROR:', error);
                            reject();
                            return;
                        })
                    }

                    // #################################################################
                    // FINISHED
                    // #################################################################
                    resolve();

                }).catch((error) => {
                    console.log('readLocalPortfolios(error):', error);
                    reject();
                });
            });
        }

        /**
         * Aktuelle Daten des heruntergeladenen Containers in der IndexedDB speichern
         * Nach erfolgreichem Download aller Containerdaten werden die aktuellen Daten des Containers 
         * in der IndexedDB gespeichet. Damit kann im ContainerController angezeigt werden, dass der
         * Katalog/Container auf dem neuesten Stand ist.
         *
         */
        const updateDownloads = async () => {
            const debug = false;

            return new Promise(async function (resolve, reject) {
                let containerFound = null;
                await getContainer(getState, container, version).then((newContainer) => {
                    debug && console.log('get container from API:', newContainer);
                    containerFound = newContainer;
                }).catch((error) => {
                    console.log('getContainer(error):', error);
                    reject();
                });

                if (!containerFound) {
                    console.log('container nicht gefunden! -> return');
                    return;
                }

                let updated = false;
                let newData = [];

                downloadList.data.forEach((d) => {
                    if (d.id.localeCompare(container) === 0) {
                        newData.push(containerFound);
                        updated = true;
                    } else {
                        newData.push(d);
                    }
                });

                if (updated === false) {
                    newData.push(containerFound);
                }

                const newDownloadList = { id: 'downloads.json', name: 'downloads.json', container: null, data: newData }

                debug && console.log('\n\nnewDownloadList:', newDownloadList);

                await cordova.writeLocalJSON(null, 'downloads.json', newDownloadList)
                    .then((data) => {
                        dispatch({ type: 'SET_DOWNLOAD_LIST', payload: newDownloadList });
                        debug && console.log('   writeLocalJSON(downloads.json) success:', data);
                        resolve();
                    })
                    .catch((error) => {
                        console.log('   writeLocalJSON(downloads.json) error:', error);
                        reject();
                    });
            })
        }

        const successCB = async () => {
            // #################################################################
            // Common-Synchronisation
            // Laden aller PDFs und Videos
            // #################################################################
            console.log('\nSTEP 4 - DOWNLOAD FILES\n');
            utils.logHeap('STEP 4');

            dispatch({ type: 'SYNC_COMMON_PENDING' });
            dispatch({ type: 'SET_SYNC_TYPE', payload: 'syncFiles' });

            let success = false;
            await downloadAllFiles().then(() => {
                //console.log('downloadAllFiles(success)');

                dispatch({ type: 'SYNC_COMMON_SUCCESS' });
                dispatch({ type: 'SET_SYNC_TYPE', payload: '' });
                success = true;
            }).catch((error) => {
                console.log('downloadAllFiles(error):', error);

                dispatch({ type: 'SYNC_COMMON_ERROR' });
                dispatch({ type: 'SET_SYNC_TYPE', payload: '' });
            })
            if (!success) return;

            // #################################################################
            // Redux Store anhand LocalStorage aktualisieren
            // Damit die Daten auch sofort in der Anwendung zur Verfügung stehen
            // Siehe auch PortfoliosController Stichwort 'SYNC'
            // #################################################################
            console.log('\nSTEP 5 - UPDATE REDUX STORE\n');
            utils.logHeap('STEP 5');

            success = false;
            await updateReduxStore().then(() => {
                console.log('updateReduxStore(success)');
                success = true;
            }).catch((error) => {
                console.log('updateReduxStore(error)', error);
            })
            if (!success) return;

            // #################################################################
            // Neue settings.json in LocalStorage ablegen
            // #################################################################
            console.log('\nSTEP 6 - UPDATE DOWNLOADS\n');
            utils.logHeap('STEP 6');

            success = false;
            await updateDownloads().then(() => {
                console.log('updateDownloads(success)');
                success = true;
            }).catch((error) => {
                console.log('updateDownloads(error)', error);
            })
            if (!success) return;

            // #################################################################
            // ENDE der Portfolio-Synchronisation
            // #################################################################

            utils.logHeap('ENDE');

            console.log(`\ndoSync(${container}, ${version}) ENDE\n\n`);

            successCallback && successCallback();
        }

        const errorCB = () => {
            // #################################################################
            // ENDE der Portfolio-Synchronisation
            // #################################################################
            dispatch({ type: 'SET_SYNC_TYPE', payload: '' });

            console.log(`\ndoSync(${container}, ${version}) ENDE durch Fehler\n\n\n`);

            errorCallback && errorCallback();
        }

        const cancelCB = () => {
            // #################################################################
            // ENDE der Portfolio-Synchronisation
            // #################################################################
            dispatch({ type: 'SET_SYNC_TYPE', payload: '' });

            console.log(`\ndoSync(${container}, ${version}) ENDE durch Abbruch\n\n\n`);

            successCallback && successCallback();
        }

        // ###############################################################
        // 
        // MAIN - SECTION
        // 
        // ###############################################################

        utils.logHeap();

        // ###############################################################
        // START der Basis-Synchronisation
        // ###############################################################

        dispatch({ type: 'SYNC_PORTFOLIO_PROGRESS', payload: null });

        dispatch({ type: 'SYNC_COMMON_PROGRESS', payload: null });

        dispatch({ type: 'SYNC_PORTFOLIO_PENDING' });

        dispatch({ type: 'SET_SYNC_TYPE', payload: 'syncPortfolio' });

        // ###############################################################
        // aktuelle portfolios aus LocalStorage lesen
        // ###############################################################
        console.log('\nSTEP 1 - DELETE OLD PORTFOLIOS\n');
        utils.logHeap('STEP 1');

        /*
        await cordova.readLocalPortfolios(container).then(async (oldPortfolioList) => {
            console.log('\n\n#####################');
            console.log('oldPortfolioList:', oldPortfolioList);
            console.log('#####################\n\n');
        })
        */

        await db.portfolio.where('container').equals(container).delete().then(() => {
            console.log('SUCCESS: delete old portfolios')
        }).catch((error) => {
            console.log('ERROR: delete old portfolios!')
        });

        // ###############################################################
        // Inhaltsverzeichnis aus 'toc.json' lesen
        // ###############################################################
        console.log('\nSTEP 2 - DOWNLOAD TOC\n');
        utils.logHeap('STEP 2');

        getPortfolioTOC().then((portfolio) => {
            const newPortfolioList = []
            portfolio.items.forEach((d) => {
                newPortfolioList.push({ ...d, id: `${container}_${d.id}` })
            });

            // ###############################################################
            // Alle Portfolios anfordern und auf Ergebnisse warten
            // ###############################################################
            console.log('\nSTEP 3 - DOWNLOAD PORTFOLIOS\n');
            utils.logHeap('STEP 3');

            doSyncAllPortfolios(dispatch, getState, container, version, newPortfolioList, cancelCB, successCB, errorCB);

        }).catch((error) => {
            console.log('getPortfolioTOC() error:', error);
            reportError(error);

            dispatch({ type: 'SYNC_PORTFOLIO_ERROR' });
        })
    }
}

/**
 * Bulk Synchronisation aller Portfolios
 *
 * Portfolios asynchron, bzw. parallel in Blöcken heruntergeladen. 
 * 
 * @param {string} container - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {string} version   - Version (z.B.: live, draft, etc.)
 * @param {array} syncList - Liste aller download Documente
 * @param {function} cancelCallback - callback wenn sync abgebrochen wurde 
 * @param {function} successCallback - callback bei erfolgreichem Download 
 * @param {function} errorCallback - callback bei fehlerhaftem Download 
 * 
 */
export const doSyncAllPortfolios = async (dispatch, getState, container, version, syncList, cancelCallback, successCallback, errorCallback) => {
    const { apiURL, token } = getState().app;
    const debug = false;

    let bulkSize = 1;
    let nextBulkStart = 0;

    let totalBytes = 0;
    let allCurrent = [];

    // ###############################################################
    // 
    // HELPER - SECTION
    // 
    // ###############################################################

    /** 
    * Gesamte Anzahl Bytes berechnen, die übertragen werden müssen. 
    */
    const getTotalBytes = () => {
        syncList.forEach(d => totalBytes += d.size);
        //console.log(`\ntotalBytes: ${utils.humanFileSize(totalBytes)}\n`);
    }

    /** 
    * Anzahl Bytes berechnen, die während der Synchronisation übertragen wurden. 
    */
    const getProgress = () => {
        let currentBytes = 0
        let totalPercent = 0

        for (let keyCurrent in allCurrent) { currentBytes += allCurrent[keyCurrent] };

        totalPercent = 100 / totalBytes * currentBytes;

        return { totalBytes, currentBytes, totalPercent };
    }

    /**
     * Eine bestimmte Portfolio JSON-Datei vom API lesen.
     *
     * @param {object} d - data - Portfolio-Datensatz
     * @resolve {JSON} Portfolio
     * @reject {object} Error-Struktur
     * @return {object} Promise-Struktur
     */
    const getPortfolioJSON = (d) => {
        const requestPromise = new Promise(function (resolve, reject) {

            utils.logHeap();

            debug && console.log('\nget ' + d.url + ' from API');

            axios({
                url: encodeURI(`${apiURL}/api/v1/file/${container}/versions/${version}/portfolios/${d.url}`),
                method: 'GET',
                responseType: 'json',
                timeout: 60 * 60 * 1000,
                headers: { 'authorization': token },
                onDownloadProgress: (progress) => {
                    allCurrent[d.url] = progress.loaded
                    dispatch({ type: 'SYNC_PORTFOLIO_PROGRESS', payload: getProgress() })
                }
            }).then((response) => {
                let newPortfolio = { ...response.data, id: d.id };

                cordova.writeLocalFile(container, 'portfolio', null, d.id, newPortfolio)
                    .then(() => {
                        utils.logHeap('');

                        debug && console.log(`put ${d.id} to localStorage\n\n`);

                        resolve(d.id);
                    })
                    .catch((error) => {
                        console.log(`   ERROR: write failed: ${d.id}`, error);
                        resolve(d.id);
                    });

            }).catch((error) => {
                if (error.response && error.response.status === 400) {
                    errorCallback()
                }
                reportError(error)
                reject(error)
            })
        })
        return requestPromise
    }

    const getNextBulk = () => {
        let bulkList = [];
        let count = 0;

        for (let i = nextBulkStart; i <= syncList.length; i++) {
            if (count >= bulkSize || syncList.length === parseInt(i)) {
                nextBulkStart += count;
                return bulkList;
            }
            bulkList.push(syncList[i]);
            count++;
        }
        return [];
    }

    const doBulkSyncPortfolios = () => {
        const { syncCancel } = getState().sync;
        let syncBulkList = getNextBulk();

        if (syncBulkList.length > 0) {
            Promise.all(syncBulkList.map(getPortfolioJSON)).then(() => {

                //#######################################
                // NEXT - download next Portfolio
                //#######################################
                if (syncCancel) {
                    console.log(`\n\nCANCEL doBulkSyncPortfolio()`);

                    cancelCallback();
                } else {
                    doBulkSyncPortfolios();
                }

            }).catch((err) => {
                console.log('error stop sync', err);

                errorCallback(err);
            })
        } else {
            // ########################################
            // FINALE
            // ########################################
            if (!syncCancel) {
                //console.log('\nportfolio sync done.\n')

                successCallback();
            }
        }
    }

    //#######################################
    // START - download first Portfolio
    //#######################################

    getTotalBytes();

    doBulkSyncPortfolios();
}

/**
 * Bulk Synchronisation aller Dateien (PDF, PNG, JPG)
 *
 * Dateien asynchron, bzw. parallel in Blöcken heruntergeladen. 
 * 
 * @param {string} container - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {string} version - Version (z.B.: draft, live)
 * @param {array} syncFiles - Liste aller download Documente
 * @param {integer} bulkSize - Anzahl Dateien die parallel geladen werden.
 * 
 */
export const doSyncFiles = async (dispatch, getState, container, version, syncFiles, bulkSize) => {
    const { apiURL, token, isSafari } = getState().app;
    const debug = false;

    return new Promise(function (resolve, reject) {
        let nextBulkStart = 0;
        let lastBytes = 0;
        let totalBytes = 0;
        let currentBytes = 0;

        // ###############################################################
        // 
        // HELPER - SECTION
        // 
        // ###############################################################

        /** 
        * Gesamte Anzahl Bytes berechnen, die übertragen werden müssen. 
        */
        const getTotalBytes = () => {
            syncFiles.forEach(d => totalBytes += d.size);
            //console.log(`\ntotalBytes: ${utils.humanFileSize(totalBytes)}\n`);
        }

        /** 
        * Anzahl Bytes berechnen, die während der Synchronisation übertragen wurden. 
        */
        const getProgress = (progress) => {
            currentBytes = Math.min(Math.max(currentBytes, lastBytes + progress.loaded), totalBytes);

            if (progress.total === progress.loaded) {
                lastBytes = currentBytes
            }

            let totalPercent = 0
            if (totalBytes * currentBytes !== 0) {
                totalPercent = (100 / totalBytes * currentBytes)
            }

            let currentPercent = 0
            if (progress.total * progress.loaded !== 0) {
                currentPercent = (100 / progress.total * progress.loaded)
            }
            return { totalBytes, currentBytes, totalPercent, currentPercent }
        }

        /**
         * Eine bestimmte Portfolio JSON-Datei vom API lesen.
         *
         * @param {object} d - data - Portfolio-Datensatz
         * @resolve {JSON} Portfolio
         * @reject {object} Error-Struktur
         * @return {object} Promise-Struktur
         */
        const getFile = (d) => {

            return new Promise(function (resolve, reject) {

                let url = '';
                let responseType = 'arraybuffer';
                let localStorage = '';
                let extension = utils.getFileExtension(d.name);

                if (['mp4'].includes(extension)) {
                    url = encodeURI(`${apiURL}/api/v1/file/${container}/common/${d.name}`);
                    if (!isSafari) responseType = 'blob';
                    localStorage = 'video';
                }

                if (['png', 'jpg'].includes(extension)) {
                    const path = d.path.replaceAll('/', ':');
                    url = encodeURI(`${apiURL}/api/v1/file/${container}/${version}/path/${path}/${d.name}`);
                    if (!window['cordova']) responseType = 'blob';
                    localStorage = 'image';
                }

                if (['pdf'].includes(extension)) {
                    const path = d.path.replaceAll('/', ':');
                    url = encodeURI(`${apiURL}/api/v1/file/${container}/${version}/path/${path}/${d.name}`);
                    localStorage = 'pdf';
                }

                if (url === '') {
                    //reject(new Error(`File-Type [${d.name}] not supported!`));
                    resolve();
                    return;
                }

                debug && console.log(`get ${d.path}/${d.name} from API`);

                axios({
                    url: url,
                    method: 'GET',
                    timeout: 60 * 60 * 1000,
                    headers: { 'authorization': token },
                    responseType: responseType,
                    onDownloadProgress: (progress) => {
                        dispatch({ type: 'SYNC_COMMON_PROGRESS', payload: getProgress(progress) });
                    }
                }).then(res => {

                    cordova.writeLocalFile(container, localStorage, d.path, d.name, res.data)
                        .then(() => {
                            debug && console.log(`put ${d.path}/${d.name} to localStorage`);
                            utils.logHeap('');
                            debug && console.log(``);

                            resolve(d.name);
                        })
                        .catch((error) => {
                            console.log(`\nwriteLocalFile(error) could't write [${d.path}/${d.name}] to localStorage`, error);
                            reject(new Error('getFile(error)', error));
                        });

                }).catch((error) => {
                    console.error(`\ngetFile(error) could't download [${d.path}/${d.name}] from API!\n\n`);
                    // Kein Abbruch, wenn eine einzelne Datei nicht gefunden wurde
                    //reject(new Error('getFile(error)', error));
                    resolve(d.name);
                })
            })
        }

        const getNextBulk = () => {
            let bulkList = [];
            let count = 0;

            for (let i = nextBulkStart; i <= syncFiles.length; i++) {
                if (count >= bulkSize || syncFiles.length === parseInt(i)) {
                    nextBulkStart += count;
                    return bulkList;
                }
                bulkList.push(syncFiles[i]);
                count++;
            }
            return [];
        }

        const doBulkSyncFinished = () => {

            console.log('\n\nFILES: doSyncFiles() loaded.\n');

            resolve();
        }

        const doBulkSyncFiles = () => {
            const { syncCancel } = getState().sync;

            let syncBulkList = getNextBulk();

            if (syncBulkList.length > 0) {
                Promise.all(syncBulkList.map(getFile)).then(() => {
                    if (syncCancel) {
                        console.log(`\n\nCANCEL doSyncFiles()`);

                        doBulkSyncFinished();
                    } else {
                        doBulkSyncFiles();
                    }

                }).catch((error) => {
                    // ACHTUNG: Kein Abbruch wenn eine einzelne Datei nicht
                    // geladen werden konnt.
                    reject(new Error('doBulkSyncFiles(error)', error));
                    //doBulkSyncFiles();
                })
            } else {
                // ########################################
                // FINALE
                // ########################################
                doBulkSyncFinished();
            }
        }

        // ###############################################################
        // 
        // MAIN - SECTION
        // 
        // ###############################################################
        console.log('\n\nFILES: doSyncFiles() loading...\n');

        // totalBytes berechnen
        getTotalBytes();

        // Initialize - prepare syncFiles
        dispatch({ type: 'SYNC_LIST', payload: syncFiles });

        //#######################################
        // START - download first Document
        //#######################################
        doBulkSyncFiles();
    });
}

/**
 * Download aller Videos
 *
 * Videso sequenziell nacheinander heruntergeladen. 
 * 
 * Diese Methode wird nur verwendet, wenn Videos auf dem iPad (CORDOVA existiert) heruntergeladen werden.
 * In der PWA-Version werdenmit der o.g. Methode doSyncFiles() auch Vidos heruntergeladen.
 * Es hat sich herausgestellt, dass das laden größere Videos auf dem iPad mit der doSyncFiles()-Methode 
 * nicht sicher geladen werden können. Aus diesem Grund wird in dieser doSyncVideos()-Methode das alte 
 * Verfahren zum Download der Videos verwendet, das schon in der Version 1.0 verwendet wurde. 
 * 
 * @param {string} container - Container/Katalog (z.B.: linde, schöler, etc.)
 * @param {array} syncFiles - Liste aller download Documente
 * 
 */
export const doSyncVideos = (dispatch, getState, container, videoList) => {
    const { apiURL } = getState().app;

    return new Promise(function (resolve, reject) {

        let videoIndex = 0;

        let lastBytes = 0;
        let totalBytes = 0;
        let currentBytes = 0;

        /** 
        * Gesamte Anzahl Bytes berechnen, die übertragen werden müssen. 
        */
        const getTotalBytes = () => {
            videoList.forEach(d => totalBytes += d.size);
            console.log(`\ntotalBytes: ${utils.humanFileSize(totalBytes)}\n`);
        }

        const onProgress = (progress) => {
            currentBytes = Math.min(Math.max(currentBytes, lastBytes + progress.bytesReceived), totalBytes);

            if (progress.totalBytesToReceive === progress.bytesReceived) {
                lastBytes = currentBytes
            }

            let totalPercent = 0
            if (totalBytes * currentBytes !== 0) {
                totalPercent = (100 / totalBytes * currentBytes)
            }

            let currentPercent = 0
            if (progress.totalBytesToReceive * progress.bytesReceived !== 0) {
                currentPercent = (100 / progress.totalBytesToReceive * progress.loaded)
            }
            dispatch({ type: 'SYNC_COMMON_PROGRESS', payload: { totalBytes, currentBytes, totalPercent, currentPercent } });
        }

        const onSuccess = () => {
            console.log('OK: download done.');

            if (videoIndex < videoList.length) {
                videoIndex++;
                downloadNext();
            }
        }

        const onError = (err) => {
            console.log('ERROR: download failed!' + err);

            if (videoIndex < videoList.length) {
                videoIndex++;
                downloadNext();
            }
        }

        const downloadNext = () => {
            const { syncCancel } = getState().sync;

            if (videoList.length === 0) {
                console.log('videoList is empty!')
                console.log('no videos to download!')

                resolve();
                return;
            }

            if (videoIndex >= videoList.length) {
                resolve();
                return;
            }

            if (syncCancel) {
                console.log(`\n\nCANCEL doSyncVideos`);
                resolve();
                return;
            }

            const d = videoList[videoIndex];
            //const url = encodeURI(`${apiURL}/api/v1/file/${container}/common/${d.name}?Authorization=${token}`);
            const url = encodeURI(`${apiURL}/api/v1/file/${container}/common/${d.name}`);

            console.log('\ndownloadNext() ----------------------------------------')
            console.log('url:', url)

            try {
                let downloader = new window.BackgroundTransfer.BackgroundDownloader();

                window.requestFileSystem(window.LocalFileSystem.PERSISTENT, 0, (fs) => {

                    fs.root.getDirectory('video', { create: true, exclusive: false }, (dirEntry) => {

                        dirEntry.getFile(d.name, { create: true }, (fileEntry) => {

                            let download = downloader.createDownload(url, fileEntry);

                            download.startAsync().then(onSuccess, onError, onProgress);

                        }, (err) => { console.log('ERROR(1):', err) });

                    }, (err) => { console.log('ERROR(2):', err) });

                }, (err) => { console.log('ERROR(3):', err) });

            } catch (err) {
                console.log('ERROR(4):' + err);
            }
        }

        // ###############################################################
        // 
        // MAIN - SECTION
        // 
        // ###############################################################

        // totalBytes berechnen
        getTotalBytes();

        // Initialize - prepare syncFiles
        dispatch({ type: 'SYNC_LIST', payload: videoList });

        videoIndex = 0;

        //#######################################
        // START - download first Document
        //#######################################
        downloadNext();
    });
}

/**
 * preloadPDFWorkerMin()
 * 
 * forciert das Laden der pdf.worker.min.js in den Cache.
 * siehe service-worker.js
 */
export const preloadPDFWorkerMin = () => {
    return (dispatch, getState) => {

        dispatch({ type: 'PRELOAD_PDF_PENDING' });

        axios({
            url: encodeURI('https://unpkg.com/pdfjs-dist@2.6.347/build/pdf.worker.min.js'),
            method: 'GET',
            timeout: 5 * 1000
        }).then((response) => {
            dispatch({ type: 'PRELOAD_PDF_SUCCESS', payload: response.data });
        }).catch((error) => {
            reportError(error)
            dispatch({ type: 'PRELOAD_PDF_ERROR' })
        });
    }
}

const reportError = (error) => {

    if (error.response) {
        if (error.response.status === 400) {
            console.log('ERROR(response): status: 400 -> Session ist abgelaufen', error.response)
        } else {
            console.log('ERROR(response): Datei konnte nicht geladen werden!')
        }

    } else if (error.request) {
        console.log('ERROR(request)', error.request)
    } else {
        console.log('ERROR(request) setup?', error.message)
    }
}

export const syncFileDiff = (sContainer, syncFiles, localFiles, sField) => {
    const debug = false;
    debug && console.log(`syncFileDiff(${sContainer}, ${sField}`, syncFiles, localFiles);
    let diffFiles = [];

    const existsInLocalFiles = (syncFile) => {
        for (const localFile of localFiles) {
            debug && console.log('   loop localFiles:', localFile);
            if (sField === 'id') {
                const sID = `${sContainer}_${utils.cyrb53(syncFile.path)}_${syncFile.name}`;
                debug && console.log('      syncFile sID:', sID)
                debug && console.log('      localFile.id:', localFile.id)
                if (sID.localeCompare(localFile.id) === 0) {
                    debug && console.log('      ------> found')
                    return true;
                }
            } else {
                if (syncFile[sField].localeCompare(localFile[sField]) === 0) return true;
            }
        }
        return false;
    }

    for (const syncFile of syncFiles) {
        debug && console.log('loop syncFiles:', syncFile);
        if (existsInLocalFiles(syncFile) === false) {
            diffFiles.push(syncFile);
        }
    }

    return diffFiles;
}

export const getContainer = (getState, sContainer, sVersion) => {
    const { apiURL, token } = getState().app;

    return new Promise(function (resolve, reject) {
        axios({
            url: encodeURI(apiURL + `/api/v1/containers/${sContainer}/${sVersion}`),
            method: 'GET',
            responseType: 'json',
            headers: { 'authorization': token, 'Content-Type': 'application/json' }
        }).then((response) => {
            resolve(response.data)
        }).catch((error) => {
            reject(error);
        });
    })
}